import React from "react";
import PropTypes from "prop-types";
import { Button } from "@toddle-edu/ds-web";
import { AddOutlined } from "@toddle-edu/ds-icons";

import classes from "./AddQuestionDropdownWrapper.scss";
import SinglePageScrollController from "../SinglePageScrollController";
import { WORKSHEET_QUESTION_CREATION } from "Constants/stringConstants";

/**
 * AddQuestionDropdownWrapper Component
 *
 * This component handles the UI for adding questions and sections at the bottom of the worksheet.
 * It conditionally renders different states:
 * 1. Add button UI when user can add questions/sections
 * 2. Scroll controller when AI is generating questions
 * 3. Nothing when neither condition is met
 *
 * The component adapts its appearance based on whether sections exist in the worksheet.
 */
const AddQuestionDropdownWrapper = ({
  t,
  hasSections,
  showAddButton,
  getAddButton,
  activeQuestionId,
  isGeneratingAIQuestions,
  containerRef,
  spaceContainerInBetweenRef,
}) => {
  // CASE 1: Show add button when user can add questions/sections
  if (showAddButton) {
    return (
      <div className={classes.addQuestionButtonContainer}>
        <div className={classes.addQuestionButton}>
          {/* Render the add button with appropriate configuration */}
          {getAddButton({
            forSidebar: true,
            isRevampedSinglePageUI: true,
            showAddInlineText: true,
            btnComponent: (
              <Button
                icon={<AddOutlined />}
                variant="neutral"
                type="outlined"
                size={"large"}
                isFullWidth={true}
                dsVersion="2.0"
                testId="worksheet-addQuestionsDropdown-singlePageEditor"
              >
                {t("common:add_with_label", {
                  label: `${t("worksheet:questions")}/${t("common:section")}`,
                })}
              </Button>
            ),
          })}
        </div>
      </div>
    );
  }

  // CASE 2: Show scroll controller when AI is generating questions
  // Only show if we're not in question creation mode and AI is actively generating
  if (
    activeQuestionId !== WORKSHEET_QUESTION_CREATION &&
    isGeneratingAIQuestions
  ) {
    return (
      <SinglePageScrollController
        containerRef={containerRef}
        activeQuestionId={activeQuestionId}
        isGeneratingAIQuestions={isGeneratingAIQuestions}
        spaceContainerInBetweenRef={spaceContainerInBetweenRef}
      />
    );
  }

  return null;
};

AddQuestionDropdownWrapper.propTypes = {
  t: PropTypes.func.isRequired,
  hasSections: PropTypes.bool.isRequired,
  showAddButton: PropTypes.bool.isRequired,
  getAddButton: PropTypes.func.isRequired,
  activeQuestionId: PropTypes.string,
  isGeneratingAIQuestions: PropTypes.bool,
  containerRef: PropTypes.object,
  spaceContainerInBetweenRef: PropTypes.object,
};

AddQuestionDropdownWrapper.defaultProps = {
  activeQuestionId: "",
  isGeneratingAIQuestions: false,
  containerRef: null,
  spaceContainerInBetweenRef: null,
};

export default AddQuestionDropdownWrapper;
